import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { adminService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import AdminCartWorkspace from '../components/AdminCartWorkspace'
import AdminFilterSelect from '../components/AdminFilterSelect'
import AdminHelpTip from '../components/AdminHelpTip'
import AdminStatusMark from '../components/AdminStatusMark'
import AdminUserWorkspace from '../components/AdminUserWorkspace'
import AppDock from '../components/AppDock'
import ConfirmModal from '../components/ConfirmModal'
import { ActivityGlyph, ShieldGlyph, UsersGlyph } from '../components/AdminIcons'
import {
  accountStatusLabel,
  adminDisplayName,
  adminInitials,
  adminPagerState,
  buildAdminUserParams,
  formatStamp,
  adminRoleState,
  isAdminDisplay,
  missingOrError,
  presenceHint,
  presenceLabel,
  roleLabel,
} from '../utils/adminPolicy'
import { adminQueryErrorText, shouldRetryAdminQuery } from '../utils/adminQueryPolicy'
import '../styles/liquidGlass.css'
import '../styles/scrollbar.css'
import './AdminPage.css'

const ROLE_OPTIONS = [
  { value: 'all', label: 'Tous les rôles' },
  { value: 'admin', label: 'Administrateurs' },
  { value: 'user', label: 'Membres' },
]

const PRESENCE_OPTIONS = [
  { value: 'all', label: 'Toutes les présences' },
  { value: 'online', label: 'En ligne' },
  { value: 'idle', label: 'Inactif' },
  { value: 'offline', label: 'Hors ligne' },
  { value: 'unknown', label: 'Présence inconnue' },
]

export { AdminStatusMark }

export function AdminRecoverableError({ error, fallback, onRetry }) {
  return (
    <div className="admin-state" role="alert">
      <p>{adminQueryErrorText(error, fallback)}</p>
      {onRetry ? <button type="button" className="admin-retry" onClick={onRetry}>Réessayer</button> : null}
    </div>
  )
}

export function AdminPager({ page, total, pageSize, hasError, onPage }) {
  const pager = adminPagerState({ page, total, pageSize, hasError })
  return (
    <div className="admin-pager">
      <button type="button" disabled={pager.prevDisabled} onClick={() => onPage(page - 1)}>
        Précédent
      </button>
      <span className="admin-pager-meta">{pager.label}</span>
      <button type="button" disabled={pager.nextDisabled} onClick={() => onPage(page + 1)}>
        Suivant
      </button>
    </div>
  )
}

export function AdminUserRow({ user, onOpen }) {
  const name = adminDisplayName(user)
  const presence = user.presence || 'unknown'
  return (
    <li>
      <button
        type="button"
        className="admin-user-row"
        onClick={() => onOpen(user.id)}
      >
        <span className="admin-person">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="" className="admin-avatar" />
          ) : (
            <span className="admin-avatar admin-avatar--fallback" aria-hidden="true">
              {adminInitials(user)}
            </span>
          )}
          <span className="admin-person-copy">
            <strong>{name}</strong>
            <small>{user.email || 'Adresse absente'}</small>
          </span>
        </span>
        <AdminStatusMark kind={user.role === 'admin' ? 'admin' : 'member'} label={roleLabel(user.role)} />
        <AdminStatusMark kind={user.account_status || 'unknown'} label={accountStatusLabel(user.account_status)} />
        <AdminStatusMark
          kind={presence}
          label={presenceLabel(presence)}
          title={presenceHint(presence, user.presence_freshness_seconds)}
        />
        {/* data-label rend la mesure lisible quand l'en-tête de colonnes
            disparaît au profit d'une liste structurée. */}
        <span className="admin-metric" data-label="Paniers">{missingOrError(user.carts_count, user.counts_error)}</span>
        <span className="admin-metric" data-label="Articles">{missingOrError(user.items_count, user.counts_error)}</span>
      </button>
    </li>
  )
}

export function AdminUsersList({ users, onOpen }) {
  return (
    <div className="admin-list">
      <div className="admin-list-head" aria-hidden="true">
        <span>Personne</span>
        <span>Rôle</span>
        <span>Compte</span>
        <span>Présence</span>
        <span>Paniers</span>
        <span>Articles</span>
      </div>
      <ul className="admin-list-body">
        {users.map((user) => (
          <AdminUserRow key={user.id} user={user} onOpen={onOpen} />
        ))}
      </ul>
    </div>
  )
}

export function AdminToolbar({ query, role, presence, onQuery, onRole, onPresence, autoOpenPresence = false }) {
  return (
    <div className="admin-toolbar">
      <input
        type="search"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        placeholder="Rechercher un nom ou une adresse"
        aria-label="Rechercher un utilisateur"
      />
      <AdminFilterSelect
        label="Filtrer par rôle"
        value={role}
        options={ROLE_OPTIONS}
        onChange={onRole}
      />
      <AdminFilterSelect
        label="Filtrer par présence"
        value={presence}
        options={PRESENCE_OPTIONS}
        onChange={onPresence}
        autoOpen={autoOpenPresence}
      />
    </div>
  )
}

function AdminPage() {
  const { user, loading, profileError } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const roleState = adminRoleState(user, { loading, profileError })
  const canDisplay = roleState === 'admin' && isAdminDisplay(user)
  const [section, setSection] = useState('users')
  const [query, setQuery] = useState('')
  const [role, setRole] = useState('all')
  const [presence, setPresence] = useState('all')
  const [page, setPage] = useState(1)
  const [auditPage, setAuditPage] = useState(1)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [selectedCartId, setSelectedCartId] = useState(null)
  const [resetNotice, setResetNotice] = useState(null)
  const [saveError, setSaveError] = useState('')
  const [addError, setAddError] = useState('')
  const [editError, setEditError] = useState('')
  const [deleteItem, setDeleteItem] = useState(null)
  const [copied, setCopied] = useState(false)
  const [saveNotice, setSaveNotice] = useState('')
  const listScrollRef = useRef(0)
  const userScrollRef = useRef(0)
  const restoreListScroll = useRef(false)
  const restoreUserScroll = useRef(false)

  const listParams = useMemo(
    () => buildAdminUserParams({ query, role, presence, page }),
    [query, role, presence, page],
  )

  const usersQuery = useQuery({
    queryKey: ['admin-users', listParams],
    queryFn: () => adminService.listUsers(listParams),
    enabled: canDisplay && section === 'users' && !selectedUserId,
    retry: shouldRetryAdminQuery,
  })

  const auditQuery = useQuery({
    queryKey: ['admin-audit', auditPage],
    queryFn: () => adminService.audit({ page: auditPage, page_size: 30 }),
    enabled: canDisplay && section === 'audit',
    retry: shouldRetryAdminQuery,
  })

  const userQuery = useQuery({
    queryKey: ['admin-user', selectedUserId],
    queryFn: () => adminService.getUser(selectedUserId),
    enabled: canDisplay && Boolean(selectedUserId),
  })

  const cartsQuery = useQuery({
    queryKey: ['admin-user-carts', selectedUserId],
    queryFn: () => adminService.listCarts(selectedUserId),
    enabled: canDisplay && Boolean(selectedUserId),
  })

  const cartQuery = useQuery({
    queryKey: ['admin-cart', selectedCartId],
    queryFn: () => adminService.getCart(selectedCartId),
    enabled: canDisplay && Boolean(selectedCartId),
  })

  const patchCart = useMutation({
    mutationFn: ({ cartId, data }) => adminService.updateCart(cartId, data),
    onSuccess: () => {
      setSaveError('')
      setSaveNotice('Panier enregistré')
      queryClient.invalidateQueries({ queryKey: ['admin-cart', selectedCartId] })
      queryClient.invalidateQueries({ queryKey: ['admin-user-carts', selectedUserId] })
    },
    onError: (error) => {
      setSaveNotice('')
      setSaveError(error.response?.data?.detail || 'Enregistrement du panier impossible')
    },
  })

  const patchItem = useMutation({
    mutationFn: ({ itemId, data }) => adminService.updateItem(itemId, data),
    onSuccess: () => {
      setEditError('')
      setSaveError('')
      setSaveNotice('Produit enregistré')
      queryClient.invalidateQueries({ queryKey: ['admin-cart', selectedCartId] })
    },
    onError: (error) => {
      setSaveNotice('')
      setEditError(error.response?.data?.detail || 'Enregistrement du produit impossible')
    },
  })

  const addItem = useMutation({
    mutationFn: (data) => adminService.addItem(selectedCartId, data),
    onSuccess: () => {
      setAddError('')
      setSaveError('')
      setSaveNotice('Produit ajouté')
      queryClient.invalidateQueries({ queryKey: ['admin-cart', selectedCartId] })
    },
    onError: (error) => setAddError(error.response?.data?.detail || 'Ajout du produit impossible'),
  })

  const removeItem = useMutation({
    mutationFn: (itemId) => adminService.deleteItem(itemId),
    onSuccess: () => {
      setDeleteItem(null)
      queryClient.invalidateQueries({ queryKey: ['admin-cart', selectedCartId] })
    },
    onError: (error) => setSaveError(error.response?.data?.detail || 'Suppression impossible'),
  })

  const resetPassword = useMutation({
    mutationFn: () => adminService.resetPassword(selectedUserId),
    onSuccess: (data) => setResetNotice(data),
    onError: (error) => setResetNotice({
      accepted: false,
      delivered: false,
      message: error.response?.data?.detail || 'Le service a refusé la demande',
    }),
  })

  const users = usersQuery.data?.users || []
  const total = usersQuery.data?.total || 0
  const pageSize = usersQuery.data?.page_size || 20
  const detail = userQuery.data
  const cartDetail = cartQuery.data
  const workspace = selectedCartId ? 'cart' : selectedUserId ? 'user' : 'list'

  useEffect(() => {
    if (restoreListScroll.current && workspace === 'list') {
      window.scrollTo(0, listScrollRef.current)
      restoreListScroll.current = false
    }
    if (restoreUserScroll.current && workspace === 'user') {
      window.scrollTo(0, userScrollRef.current)
      restoreUserScroll.current = false
    }
  }, [workspace, detail, cartDetail])

  const copyId = async (value) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  const openUser = (id) => {
    listScrollRef.current = window.scrollY
    setSelectedUserId(id)
    setSelectedCartId(null)
    setResetNotice(null)
    setCopied(false)
    setSaveError('')
    setSaveNotice('')
  }

  const openCart = (id) => {
    userScrollRef.current = window.scrollY
    setSelectedCartId(id)
    setSaveError('')
    setSaveNotice('')
    setAddError('')
    setEditError('')
  }

  const backToUser = () => {
    restoreUserScroll.current = true
    setSelectedCartId(null)
    setSaveError('')
    setSaveNotice('')
  }

  const backToList = () => {
    restoreListScroll.current = true
    setSelectedCartId(null)
    setSelectedUserId(null)
    setResetNotice(null)
  }

  return (
    <div className="admin-page">
      <div className={`lg-panel admin-shell${workspace === 'list' ? '' : ' admin-shell--workspace'}`}>
        <div className="lg-panel-frost" aria-hidden="true" />
        <div className="lg-panel-tint" aria-hidden="true" />
        <div className="lg-panel-shine" aria-hidden="true" />
        <div className={`lg-panel-body admin-body${workspace === 'list' ? '' : ' admin-body--workspace'}`}>
          <header className="admin-header">
            <p className="admin-kicker">
              <ShieldGlyph />
              <span>Administration</span>
            </p>
            <h1>Panneau d’administration</h1>
            <p className="admin-lead">
              Les modifications s’appliquent aux données du propriétaire.
              <AdminHelpTip label="Précision sur la présence">
                La présence actuelle n’est pas la dernière connexion. Un signal manquant reste une présence inconnue (expiration 75 s / 10 min).
              </AdminHelpTip>
            </p>
          </header>

          {roleState === 'loading' ? (
            <p className="admin-state">Vérification des droits administrateur…</p>
          ) : roleState === 'profile_error' ? (
            <p className="admin-state">
              Impossible de récupérer le profil. Ce n’est pas un refus de droits.
              {profileError ? ` ${profileError}` : ''}
            </p>
          ) : roleState === 'not_admin' || !canDisplay ? (
            <p className="admin-state">Cette page n’est visible que pour un compte administrateur.</p>
          ) : workspace === 'user' ? (
            userQuery.isError ? (
              <AdminRecoverableError
                error={userQuery.error}
                fallback="Fiche utilisateur indisponible."
                onRetry={() => userQuery.refetch()}
              />
            ) : detail ? (
              <AdminUserWorkspace
                detail={detail}
                carts={cartsQuery.data || []}
                cartsError={cartsQuery.isError}
                resetNotice={resetNotice}
                resetPending={resetPassword.isPending}
                onReset={() => resetPassword.mutate()}
                onOpenCart={openCart}
                onCopyId={copyId}
                copied={copied}
                onBack={backToList}
              />
            ) : (
              <p className="admin-state">Chargement de la fiche…</p>
            )
          ) : workspace === 'cart' ? (
            cartQuery.isError ? (
              <AdminRecoverableError
                error={cartQuery.error}
                fallback="Panier introuvable."
                onRetry={() => cartQuery.refetch()}
              />
            ) : cartDetail ? (
              <AdminCartWorkspace
                key={selectedCartId}
                detail={cartDetail}
                saveError={saveError}
                saveNotice={saveNotice}
                busy={patchCart.isPending}
                addPending={addItem.isPending}
                editPending={patchItem.isPending}
                addError={addError}
                editError={editError}
                onSaveCart={(data) => patchCart.mutate({ cartId: selectedCartId, data })}
                onSaveItem={(itemId, data) => patchItem.mutate({ itemId, data })}
                onAddItem={(data) => addItem.mutate(data)}
                onAskDelete={setDeleteItem}
                onBack={backToUser}
              />
            ) : (
              <p className="admin-state">Chargement du panier…</p>
            )
          ) : (
            <>
              <nav className="admin-nav" aria-label="Sections administration">
                <button type="button" className={section === 'users' ? 'is-active' : ''} onClick={() => { setSection('users'); setPage(1) }}>
                  <UsersGlyph /> Utilisateurs
                </button>
                <button
                  type="button"
                  className={section === 'audit' ? 'is-active' : ''}
                  onClick={() => { setSection('audit'); setAuditPage(1) }}
                >
                  <ActivityGlyph /> Activité administrative
                </button>
              </nav>

              {section === 'users' ? (
                <>
                  <AdminToolbar
                    query={query}
                    role={role}
                    presence={presence}
                    onQuery={(value) => { setQuery(value); setPage(1) }}
                    onRole={(value) => { setRole(value); setPage(1) }}
                    onPresence={(value) => { setPresence(value); setPage(1) }}
                  />
                  {usersQuery.isLoading ? (
                    <p className="admin-state">Chargement des utilisateurs…</p>
                  ) : usersQuery.isError ? (
                    <AdminRecoverableError
                      error={usersQuery.error}
                      fallback="Impossible de charger la liste. Les compteurs n’ont pas été calculés."
                      onRetry={() => usersQuery.refetch()}
                    />
                  ) : users.length ? (
                    <AdminUsersList users={users} onOpen={openUser} />
                  ) : (
                    <p className="admin-state">Aucun utilisateur pour ces filtres.</p>
                  )}
                  <p className="admin-footnote">
                    {usersQuery.isError ? '—' : total} profils
                    <AdminHelpTip label="Précision sur les compteurs">
                      Paniers = propriétaires. Articles = lignes produit, pas les unités.
                    </AdminHelpTip>
                  </p>
                  <AdminPager
                    page={page}
                    total={total}
                    pageSize={pageSize}
                    hasError={usersQuery.isError}
                    onPage={setPage}
                  />
                </>
              ) : (
                <>
                  {auditQuery.isLoading ? (
                    <p className="admin-state">Chargement du journal…</p>
                  ) : auditQuery.isError ? (
                    <AdminRecoverableError
                      error={auditQuery.error}
                      fallback="Journal administratif indisponible."
                      onRetry={() => auditQuery.refetch()}
                    />
                  ) : (
                    <div className="admin-audit">
                      <div className="admin-list-head admin-audit-head" aria-hidden="true">
                        <span>Date</span>
                        <span>Action</span>
                        <span>Résultat</span>
                        <span>Cible</span>
                      </div>
                      <ul className="admin-audit-body">
                        {(auditQuery.data?.entries || []).map((entry) => (
                          <li key={entry.id}>
                            <span>{formatStamp(entry.created_at)}</span>
                            <strong>{entry.action}</strong>
                            <span>{entry.result}</span>
                            <small>{entry.resource_type} {entry.resource_id || entry.target_user_id || '—'}</small>
                          </li>
                        ))}
                      </ul>
                      {!(auditQuery.data?.entries || []).length ? <p className="admin-state">Aucune entrée dans le journal.</p> : null}
                    </div>
                  )}
                  <AdminPager
                    page={auditPage}
                    total={auditQuery.data?.total || 0}
                    pageSize={30}
                    hasError={auditQuery.isError}
                    onPage={setAuditPage}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={Boolean(deleteItem)}
        variant="delete"
        subject={deleteItem?.name}
        onClose={() => setDeleteItem(null)}
        onConfirm={() => removeItem.mutate(deleteItem.id)}
        busy={removeItem.isPending}
        error={removeItem.isError ? saveError : ''}
      />

      <AppDock
        selectedApp="settings"
        onAppSelect={(appId) => navigate('/carts', { state: { activeApp: appId } })}
      />
    </div>
  )
}

export default AdminPage
